import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { Search, Clear, FilterListOff } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { adAccountsService } from '../services/ad-accounts.service';

export interface EntityFilters {
  search: string;
  status: string; // '' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED' | 'DELETED'
  adAccountId: string; // '' | <ad_account_id>
}

interface Props {
  filters: EntityFilters;
  onFiltersChange: (filters: EntityFilters) => void;
  showAdAccount?: boolean;
  /** Optional extra slot before the search box (e.g. Campaign filter on AdSets page) */
  extraFilters?: React.ReactNode;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'DELETED', label: 'Deleted' },
];

export default function EntityListFilters({ filters, onFiltersChange, showAdAccount = true, extraFilters }: Props) {
  // Debounce the search input so we don't fire a query on every keystroke
  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFiltersChange({ ...filters, search: searchInput });
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Keep input in sync if external filter clear
  useEffect(() => {
    if (filters.search !== searchInput) setSearchInput(filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const { data: adAccounts } = useQuery({
    queryKey: ['ad-accounts', 'for-filter'],
    queryFn: () => adAccountsService.query({ limit: 100 }),
    enabled: showAdAccount,
  });

  const hasActiveFilters = !!(filters.search || filters.status || filters.adAccountId);

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      {extraFilters}

      <TextField
        size="small"
        placeholder="Search by name..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ minWidth: 220 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchInput ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchInput('')}>
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      <Autocomplete
        size="small"
        sx={{ minWidth: 170 }}
        options={STATUS_OPTIONS}
        getOptionLabel={(o) => o.label}
        value={STATUS_OPTIONS.find(s => s.value === filters.status) || STATUS_OPTIONS[0]}
        onChange={(_, v) => onFiltersChange({ ...filters, status: v?.value || '' })}
        disableClearable={!filters.status}
        renderInput={(params) => <TextField {...params} label="Status" />}
      />

      {showAdAccount && (
        <Autocomplete
          size="small"
          sx={{ minWidth: 220 }}
          options={adAccounts?.data || []}
          getOptionLabel={(o) => o.name}
          value={(adAccounts?.data || []).find(a => a.id === filters.adAccountId) || null}
          onChange={(_, v) => onFiltersChange({ ...filters, adAccountId: v?.id || '' })}
          renderInput={(params) => <TextField {...params} label="Ad Account" placeholder="All ad accounts" />}
        />
      )}

      {hasActiveFilters && (
        <Tooltip title="Clear all filters">
          <IconButton
            size="small"
            onClick={() => onFiltersChange({ search: '', status: '', adAccountId: '' })}
          >
            <FilterListOff fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
